import _defineProperty from "/app/node_modules/.pnpm/next@13.4.17_@babel+core@7.14.3_@opentelemetry+api@1.4.1_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useEffect, useRef, useState, useCallback } from 'react';
import { useFloating, offset, flip, shift, Card, Button, Chip, Text, Spacing, Icon } from '@vinted/web-ui';
import { noop, isFunction } from 'lodash';
import { Dropdown16, ChevronUp16, ChevronDown16 } from '@vinted/monochrome-icons';
import OutsideClick from 'components/OutsideClick';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DROPDOWN_MIN_WIDTH = 240;

const FilterDropdown = _ref => {
  let {
    children,
    closeOnClick,
    title = '',
    placement = 'bottom-start',
    minWidth = DROPDOWN_MIN_WIDTH,
    areAnyOfItemsSelected = false,
    useChipAsTrigger = false,
    useForceClose = false,
    testId = 'filter-dropdown',
    renderTrigger,
    onOpen = noop,
    onClose = noop,
    onFilterClick = noop
  } = _ref;
  const {
    0: isOpen,
    1: setIsOpen
  } = useState(false);
  const isInitialRender = useRef(true);
  const {
    floaterRef,
    triggerRef,
    floaterStyle
  } = useFloating({
    middleware: [offset(Spacing.Medium), flip({
      mainAxis: false
    }), shift()],
    isFloaterVisible: isOpen,
    shouldUpdateOnAncestorScroll: false,
    shouldAutoUpdate: true,
    placement
  });
  const close = useCallback(() => {
    if (!isOpen) return; // Since onOutsideClick fires via mousedown event, events inside the dropdown
    // may not fire in time (for example, onBlur). Adding a timeout of 0 makes
    // the content of the dropdown disappear only on the next tick.

    setTimeout(() => {
      setIsOpen(false);
    }, 0);
  }, [isOpen]);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    const callback = isOpen ? onOpen : onClose;
    callback();
  }, [isOpen, onOpen, onClose]);
  useEffect(() => {
    if (useForceClose) close();
  }, [useForceClose, close]);

  const toggle = () => {
    setIsOpen(prevIsOpen => !prevIsOpen);
    if (onFilterClick) onFilterClick();
  };

  const renderTriggerElement = () => {
    const sharedTriggerProps = {
      text: title,
      onClick: toggle,
      ref: triggerRef,
      testId: "".concat(testId, "--trigger")
    };

    const buttonProps = _objectSpread(_objectSpread({}, sharedTriggerProps), {}, {
      styling: Button.Styling.Flat,
      size: Button.Size.Medium,
      theme: 'muted',
      icon: /*#__PURE__*/_jsx(Icon, {
        name: Dropdown16,
        color: Icon.Color.GreyscaleLevel3
      }),
      iconPosition: Button.IconPosition.Right
    });

    if (renderTrigger && isFunction(renderTrigger)) {
      return renderTrigger({
        toggle,
        buttonProps,
        title,
        isOpen
      });
    }

    if (!useChipAsTrigger) return /*#__PURE__*/_jsx(Button, _objectSpread({}, buttonProps));
    return /*#__PURE__*/_jsx(Chip, _objectSpread(_objectSpread({}, sharedTriggerProps), {}, {
      textType: Text.Type.Subtitle,
      clicked: isOpen,
      activated: areAnyOfItemsSelected,
      suffix: /*#__PURE__*/_jsx(Icon, {
        name: isOpen ? ChevronUp16 : ChevronDown16
      }),
      aria: {
        'aria-expanded': isOpen
      }
    }));
  };

  const renderContent = () => {
    if (!isOpen) return null;
    const handleOnClick = closeOnClick ? close : undefined;
    return /*#__PURE__*/_jsx("div", {
      className: "u-fill-width u-zindex-large",
      style: _objectSpread({
        minWidth
      }, floaterStyle),
      onClick: handleOnClick,
      "data-testid": "dropdown-content",
      ref: floaterRef,
      children: /*#__PURE__*/_jsx(Card, {
        styling: Card.Styling.Elevated,
        children: children
      })
    });
  };

  return /*#__PURE__*/_jsx(OutsideClick, {
    onOutsideClick: close,
    children: /*#__PURE__*/_jsxs("div", {
      className: "u-position-relative",
      children: [renderTriggerElement(), renderContent()]
    })
  });
};

export default FilterDropdown;